import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Upload, GripVertical, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { useTranslation } from '../../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = [
  'Compass','Building2','Home','Briefcase','Paintbrush','Wrench','Zap',
  'Droplets','Hammer','Layers','ThermometerSun','Shield','Star','Settings',
  'Tool','Package','Truck','Users','Award','CheckCircle'
];

const CATEGORY_LABELS = { main: 'Основна дейност', construction: 'Строителна услуга' };

export default function AdminServices() {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, data: null });

  useEffect(() => { fetchServices(); }, []);

  const fetchServices = async () => {
    try {
      const r = await axios.get(`${API}/services`);
      setServices(r.data || []);
    } catch { toast.error('Грешка при зареждане'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Изтриване на услугата?')) return;
    try {
      await axios.delete(`${API}/services/${id}`);
      toast.success('Услугата е изтрита');
      fetchServices();
    } catch { toast.error('Грешка при изтриване'); }
  };

  const handleSave = async (formData) => {
    try {
      if (formData.id) {
        await axios.put(`${API}/services/${formData.id}`, formData);
        toast.success('Услугата е обновена');
      } else {
        await axios.post(`${API}/services`, { ...formData, order: services.length });
        toast.success('Услугата е създадена');
      }
      setModal({ open: false, data: null });
      fetchServices();
    } catch { toast.error('Грешка при запазване'); }
  };

  const mainServices = services.filter(s => s.category === 'main');
  const constructionServices = services.filter(s => s.category === 'construction');

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold">{t('services_admin.title')}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t('services_admin.subtitle')}</p>
        </div>
        <Button onClick={() => setModal({ open: true, data: null })} className="bg-[#1B5E37] hover:bg-[#154a2c]">
          <Plus className="w-4 h-4 mr-2" /> Нова услуга
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : (
        <div className="space-y-8">
          {/* Main Services */}
          <div>
            <h2 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <Badge className="bg-[#1B5E37] text-white">Основни дейности</Badge>
              <span className="text-sm text-muted-foreground font-normal">— показват се с снимка</span>
            </h2>
            {mainServices.length === 0 ? (
              <p className="text-muted-foreground text-sm py-4 border rounded-lg text-center">Няма добавени основни дейности</p>
            ) : (
              <div className="space-y-3">
                {mainServices.map(s => (
                  <ServiceRow key={s.id} service={s}
                    onEdit={() => setModal({ open: true, data: s })}
                    onDelete={() => handleDelete(s.id)} />
                ))}
              </div>
            )}
          </div>

          {/* Construction Services */}
          <div>
            <h2 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <Badge variant="secondary">Строителни услуги</Badge>
              <span className="text-sm text-muted-foreground font-normal">— показват се с иконка</span>
            </h2>
            {constructionServices.length === 0 ? (
              <p className="text-muted-foreground text-sm py-4 border rounded-lg text-center">Няма добавени строителни услуги</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {constructionServices.map(s => (
                  <ServiceRow key={s.id} service={s} compact
                    onEdit={() => setModal({ open: true, data: s })}
                    onDelete={() => handleDelete(s.id)} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <ServiceModal
        key={modal.data?.id || 'new'}
        open={modal.open}
        data={modal.data}
        onClose={() => setModal({ open: false, data: null })}
        onSave={handleSave}
      />
    </div>
  );
}

function ServiceRow({ service, onEdit, onDelete, compact }) {
  return (
    <div className={`flex items-center gap-4 p-4 bg-white border border-border rounded-xl hover:shadow-sm transition-shadow ${compact ? 'gap-3' : ''}`}>
      <GripVertical className="w-4 h-4 text-muted-foreground flex-none opacity-40" />
      {service.image && !compact ? (
        <img src={service.image} alt={service.title} className="w-16 h-16 object-cover rounded-lg flex-none" />
      ) : (
        <div className="w-10 h-10 bg-[#1B5E37]/10 rounded-lg flex items-center justify-center flex-none">
          <span className="text-[#1B5E37] text-xs font-bold">{service.icon?.slice(0,2)}</span>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{service.title}</p>
        {!compact && <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{service.description}</p>}
      </div>
      <div className="flex items-center gap-2 flex-none">
        <Button variant="ghost" size="sm" onClick={onEdit}><Pencil className="w-4 h-4" /></Button>
        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={onDelete}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function ServiceModal({ open, data, onClose, onSave }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ title: '', description: '', icon: 'Wrench', image: '', category: 'main' });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (data) setForm({ title: data.title||'', description: data.description||'', icon: data.icon||'Wrench', image: data.image||'', category: data.category||'main' });
    else setForm({ title: '', description: '', icon: 'Wrench', image: '', category: 'main' });
  }, [data, open]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/upload?category=service`, fd);
      const url = r.data.url?.startsWith('http') ? r.data.url : `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      setForm(f => ({ ...f, image: url }));
      toast.success('Снимката е качена');
    } catch { toast.error('Грешка при качване'); }
    setUploading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{data ? 'Редактиране на услуга' : 'Нова услуга'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <Label>{t('props.category')}</Label>
            <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="main">Основна дейност (с снимка)</SelectItem>
                <SelectItem value="construction">Строителна услуга (с иконка)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Заглавие *</Label>
            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Проектиране и дизайн" />
          </div>

          <div>
            <Label>{t('admin.description')}</Label>
            <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} placeholder="Кратко описание на услугата..." />
          </div>

          <div>
            <Label>{t('services_admin.icon')}</Label>
            <Select value={form.icon} onValueChange={v => setForm(f => ({ ...f, icon: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ICONS.map(icon => (
                  <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Снимка {form.category === 'main' ? '(препоръчително)' : '(по желание)'}</Label>
            {form.image && (
              <div className="relative mb-2">
                <img src={form.image} alt="" className="w-full h-36 object-cover rounded-lg" />
                <button type="button" onClick={() => setForm(f => ({ ...f, image: '' }))}
                  className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs flex items-center justify-center">✕</button>
              </div>
            )}
            <label className="flex items-center gap-2 px-4 py-2 border border-dashed border-border rounded-lg hover:border-[#1B5E37] cursor-pointer transition-colors w-fit">
              <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
              <Upload className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{uploading ? 'Качване...' : 'Качи снимка'}</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={onClose}>{t('admin.cancel')}</Button>
            <Button className="bg-[#1B5E37] hover:bg-[#154a2c]" onClick={() => onSave({ ...form, id: data?.id })}
              disabled={!form.title || uploading}>
              {data ? 'Запази' : 'Създай'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
